import { Injectable, inject } from '@angular/core';
import { AuthService } from './auth.service';

/**
 * @file roles.service.ts
 * @description Servicio para gestionar y verificar roles de usuario en la aplicación
 */
@Injectable({
  providedIn: 'root',
})
export class RolesService {
  private authService = inject(AuthService);

  /**
   * Constantes de roles disponibles en la aplicación
   */
  public readonly ROLES = {
    Administrador: 'Usuarios.Administrador',
    SecretariaEjecutiva: 'Usuarios.SecretariaEjecutiva',
    Empleado: 'Usuarios.Empleado',
    EmpleadoSE: 'Usuarios.EmpleadoSE',
    EmpleadoJER: 'Usuarios.EmpleadoJER',
    Titular: 'Usuarios.Titular',
    TitularJer: 'Usuarios.TitularJER',
    Ddispe: 'Usuarios.DDISPE',
    Consulta: 'Usuarios.Consulta',
  };

  /**
   * Verifica si el usuario actual tiene un rol específico
   * @param {string} role - Rol a verificar
   * @returns {boolean} true si el usuario tiene el rol, false en caso contrario
   */
  public hasRole(role: string): boolean {
    const userInfo = this.authService.getUserInfo();

    if (!userInfo) return false;

    const userRoles = userInfo.roles || [];
    return userRoles.includes(role);
  }

  /**
   * Verifica si el usuario actual tiene al menos uno de los roles especificados
   * @param {string[]} roles - Lista de roles a verificar
   * @returns {boolean} true si el usuario tiene al menos uno de los roles, false en caso contrario
   */
  public hasAnyRole(roles: string[]): boolean {
    return roles.some((role) => this.hasRole(role));
  }

  /**
   * Verifica si el usuario actual tiene todos los roles especificados
   * @param {string[]} roles - Lista de roles a verificar
   * @returns {boolean} true si el usuario tiene todos los roles, false en caso contrario
   */
  public hasAllRoles(roles: string[]): boolean {
    return roles.every((role) => this.hasRole(role));
  }

  /**
   * Verifica si el usuario es administrador
   * @returns {boolean} true si el usuario es administrador, false en caso contrario
   */
  public isAdmin(): boolean {
    return this.hasRole(this.ROLES.Administrador);
  }

  /**
   * Verifica si el usuario es secretaria ejecutiva
   * @returns {boolean} true si el usuario es secretaria ejecutiva, false en caso contrario
   */
  public isSecretariaEjecutiva(): boolean {
    return this.hasRole(this.ROLES.SecretariaEjecutiva);
  }

  /**
   * Verifica si el usuario es empleado
   * @returns {boolean} true si el usuario es empleado, false en caso contrario
   */
  public isEmpleado(): boolean {
    return this.hasRole(this.ROLES.Empleado);
  }

  /**
   * Verifica si el usuario es empleado de la secretaría ejecutiva
   * @returns {boolean} true si el usuario es empleado, false en caso contrario
   */
  public isEmpleadoSE(): boolean {
    return this.hasRole(this.ROLES.EmpleadoSE);
  }

  /**
   * Verifica si el usuario es empleado de la JER
   * @returns {boolean} true si el usuario es empleado, false en caso contrario
   */
  public isEmpleadoJER(): boolean {
    return this.hasRole(this.ROLES.EmpleadoJER);
  }

  /**
   * Verifica si el usuario es titular
   * @returns {boolean} true si el usuario es titular, false en caso contrario
   */
  public isTitular(): boolean {
    return this.hasRole(this.ROLES.Titular);
  }

  /**
   * Verifica si el usuario es titular de una JER
   * @returns {boolean} true si el usuario es titular, false en caso contrario
   */
  public isTitularJER(): boolean {
    return this.hasRole(this.ROLES.TitularJer);
  }

  /**
   * Verifica si el usuario es DDISPE
   * @returns {boolean} true si el usuario es DDISPE, false en caso contrario
   */
  public isDdispe(): boolean {
    return this.hasRole(this.ROLES.Ddispe);
  }

  /**
   * Verifica si el usuario es Consulta
   * @returns {boolean} true si el usuario es Consulta, false en caso contrario
   */
  public isConsulta(): boolean {
    return this.hasRole(this.ROLES.Consulta);
  }
}
